import {
  isAbortError,
  fromDocumentListData,
  fromDocumentListDataList,
  fromPatientData,
  fromPatientCodesData,
  fromPatientDocumentData,
  fromPatientRecordData,
  toDocumentUpdateRequest,
} from '@medprompt/shared';

async function fetchDocumentByPath(apiClient, documentId, pathSuffix, options = {}) {
  if (!documentId || typeof documentId !== 'string') {
    throw new Error(`Invalid documentId provided to fetch document ${pathSuffix}`);
  }

  const data = await apiClient.get(`/api/document/${documentId}/${pathSuffix}`, options);

  if (!data || typeof data !== 'object' || !data.documentId) {
    throw new Error('Invalid document data structure received');
  }

  if (data.documentId !== documentId) {
    throw new Error(`Document ID mismatch: requested ${documentId}, received ${data.documentId}`);
  }

  return data;
}

function asDocumentList(data) {
  return Array.isArray(data) ? fromDocumentListDataList(data) : [];
}

/**
 * DocumentService - Handles document-related API operations
 * Uses ApiClient for authenticated requests with proper queueing
 */
export class DocumentService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('DocumentService initialized');
  }

  /**
   * Update a document with the specified changes
   * @param {string} documentId 
   * @param {Object} updateData
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocument(documentId, updateData) {
    return this.apiClient.post(`/api/document/${documentId}/update`, toDocumentUpdateRequest(updateData));
  }

  /**
   * Update the NHS ID for a document
   * @param {string} documentId 
   * @param {string} nhsId 
   * @returns {Promise<Object>} Updated document data
   */
  async updateNhsId(documentId, nhsId) {
    return this.updateDocument(documentId, { nhsId });
  }

  /**
   * Update the brief summary for a document
   * @param {string} documentId 
   * @param {string} summary 
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentSummary(documentId, summary) {
    return this.updateDocument(documentId, { briefSummary: summary });
  }

  /**
   * Update the existing patient codes for a document
   * @param {string} documentId 
   * @param {Array} existingPatientCodes
   * @returns {Promise<Object>} Updated document data
   */
  async addExistingPatientCodes(documentId, existingPatientCodes) {
    return this.updateDocument(documentId, { existingPatientCodes });
  }

  /**
   * Update the new patient codes for a document
   * @param {string} documentId 
   * @param {Array} newPatientCodes
   * @returns {Promise<Object>} Updated document data
   */
  async addNewPatientCodes(documentId, newPatientCodes) {
    return this.updateDocument(documentId, { newCodes: newPatientCodes });
  }

  /**
   * Update the assignee for a document
   * @param {string} documentId 
   * @param {string} userId 
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentAssignee(documentId, userId) {
    return this.updateDocument(documentId, { assigneeId: userId });
  }

  /**
   * Add new outcome tasks to a document
   * @param {string} documentId
   * @param {Array} outcomeTasks - Array of OutcomeTaskRequest objects { text, taskType, assigneeId, isUrgent }
   * @returns {Promise<Object>} Updated document data
   */
  async addOutcomeTasks(documentId, outcomeTasks) {
    return this.updateDocument(documentId, { outcomeTasks });
  }

  /**
   * Update a task for a document
   * @param {string} documentId 
   * @param {string} taskId 
   * @param {Object} updates - Task updates (taskType, assigneeId, text, isSelected, isDeselected)
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentTask(documentId, taskId, updates) {
    const taskUpdate = { taskId, ...updates };
    return this.updateDocument(documentId, { taskUpdate });
  }

  /**
   * Update a measurement for a document
   * @param {string} documentId 
   * @param {string} measurementId 
   * @param {Object} updates - Measurement updates (value, unit, isSelected, isDeselected)
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentMeasurement(documentId, measurementId, updates) {
    const measurementUpdate = { measurementId, ...updates };
    return this.updateDocument(documentId, { measurementUpdate });
  }

  /**
   * Update document details (date, letter type, clinical department, document type field, sender, recipient)
   * @param {string} documentId
   * @param {Object} detailsUpdate - Partial DocumentDetails object with fields to update
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentDetails(documentId, detailsUpdate) {
    return this.updateDocument(documentId, { detailsUpdate });
  }

  /**
   * Update an existing code for a document
   * @param {string} documentId 
   * @param {string} codeId 
   * @param {Object} updates - Code updates (isSelected, isDeselected, significance, description, duration)
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentExistingCode(documentId, codeId, updates) {
    const existingCodeUpdate = { codeId, ...updates };
    
    // Special handling for duration
    if (updates.duration != null) {
      existingCodeUpdate.didChangeDuration = true;
    }
    
    return this.updateDocument(documentId, { existingCodeUpdate });
  }

  /**
   * Update a new code for a document
   * @param {string} documentId 
   * @param {string} codeId 
   * @param {Object} updates - Code updates (isSelected, isDeselected, significance, description, duration)
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentNewCode(documentId, codeId, updates) {
    const newCodeUpdate = { codeId, ...updates };
    
    // Special handling for duration
    if (updates.duration != null) {
      newCodeUpdate.didChangeDuration = true;
    }
    
    return this.updateDocument(documentId, { newCodeUpdate });
  }

  /**
   * Update a frequent code for a document
   * @param {string} documentId
   * @param {string} codeId
   * @param {Object} updates - Code updates (isSelected, isDeselected, significance, description, duration)
   * @returns {Promise<Object>} Updated document data
   */
  async updateDocumentFrequentCode(documentId, codeId, updates) {
    const frequentCodeUpdate = { codeId, ...updates };

    if (updates.duration != null) {
      frequentCodeUpdate.didChangeDuration = true;
    }

    return this.updateDocument(documentId, { frequentCodeUpdate });
  }

  /**
   * Delete existing codes from a document
   * @param {string} documentId
   * @param {Array<string>} codeIds - ExistingCode codeId values to delete
   * @returns {Promise<Object>} Updated document data
   */
  async deleteExistingCodes(documentId, codeIds) {
    return this.updateDocument(documentId, { deletedExistingCodes: codeIds });
  }

  /**
   * Delete new/suggested codes from a document
   * @param {string} documentId
   * @param {Array<string>} codeIds - NewCode codeId values to delete
   * @returns {Promise<Object>} Updated document data
   */
  async deleteNewCodes(documentId, codeIds) {
    return this.updateDocument(documentId, { deletedNewCodes: codeIds });
  }

  /**
   * Delete a document
   * @param {string} documentId 
   * @returns {Promise<Object>} Response data
   */
  async deleteDocument(documentId) {
    return this.apiClient.post(`/api/document/${documentId}/delete`);
  }

  /**
   * File away a document
   * @param {string} documentId 
   * @param {number|null} fileAwayDate - Optional file away date as UTC-midnight epoch millis (defaults to now on server)
   * @param {boolean} shouldIncludeTasks - Whether to include outcome tasks when filing away
   * @param {boolean} shouldIncludeComms - Whether to include communications when filing away
   * @param {Array<string>|null} includedCommentIds - Optional array of comment IDs to include
   * @returns {Promise<Object>} Response data
   */
  async fileAwayDocument(documentId, fileAwayDate = null, shouldIncludeTasks, shouldIncludeComms, includedCommentIds = null) {
    const requestBody = {
      shouldIncludeTasks: shouldIncludeTasks === true,
      shouldIncludeComms: shouldIncludeComms !== false  // Explicit boolean - VERY IMPORTANT
    };
    
    if (fileAwayDate != null) {
      requestBody.fileAwayDate = fileAwayDate;
    }
    
    if (includedCommentIds && includedCommentIds.length > 0) {
      requestBody.includedCommentIds = includedCommentIds;
    }
    
    return this.apiClient.post(`/api/document/${documentId}/file_away`, requestBody);
  }

  /**
   * Fetch your documents
   * @returns {Promise<Array>} Array of documents
   */
  async fetchYourDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/assigned/fetch', options);

      if (data === null || data === undefined) {
        console.error('fetchYourDocuments received null/undefined data');
        return [];
      }

      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching your documents:', error);
      throw error;
    }
  }

  /**
   * Fetch all documents
   * @deprecated Use fetchActiveDocuments() instead - this endpoint fetches too much data
   * @returns {Promise<Array>} Array of documents
   */
  async fetchDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/fetch', options);
      
      // Ensure data is always an array
      if (data === null || data === undefined) {
        console.error('fetchDocuments received null/undefined data');
        return [];
      }
      
      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  /**
   * Fetch active documents (for users with DOCS_READ_ALL viewing all documents)
   * @returns {Promise<Array>} Array of active documents
   */
  async fetchActiveDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/active/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchActiveDocuments received null/undefined data');
        return [];
      }
      
      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching active documents:', error);
      throw error;
    }
  }

  /**
   * Fetch pending documents (PENDING or GENERATING status); requires DOCS_READ_ALL on the server
   * @returns {Promise<Array>} Array of pending documents
   */
  async fetchPendingDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/pending/fetch', options);

      if (data === null || data === undefined) {
        console.error('fetchPendingDocuments received null/undefined data');
        return [];
      }

      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching pending documents:', error);
      throw error;
    }
  }

  /**
   * Fetch unassigned documents (no assignee); requires DOCS_READ_ALL on the server
   * @returns {Promise<Array>} Array of unassigned documents
   */
  async fetchUnassignedDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/unassigned/fetch', options);

      if (data === null || data === undefined) {
        console.error('fetchUnassignedDocuments received null/undefined data');
        return [];
      }

      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching unassigned documents:', error);
      throw error;
    }
  }

  /**
   * Fetch unmatched documents (READY, missing nhsId or owningPatient); requires DOCS_READ_ALL on the server
   * @returns {Promise<Array>} Array of unmatched documents
   */
  async fetchUnmatchedDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/unmatched/fetch', options);

      if (data === null || data === undefined) {
        console.error('fetchUnmatchedDocuments received null/undefined data');
        return [];
      }

      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching unmatched documents:', error);
      throw error;
    }
  }

  /**
   * Fetch archived documents
   * @returns {Promise<Array>} Array of archived documents
   */
  async fetchArchiveDocuments(options = {}) {
    try {
      const data = await this.apiClient.get('/api/documents/archive/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchArchiveDocuments received null/undefined data');
        return [];
      }
      
      return asDocumentList(data);
    } catch (error) {
      console.error('Error fetching archive documents:', error);
      throw error;
    }
  }

  /**
   * Fetch data for a specific document
   * @param {string} documentId 
   * @returns {Promise<Object>} Document data
   */
  async fetchDocumentData(documentId, options = {}) {
    const data = await fetchDocumentByPath(this.apiClient, documentId, 'data', options);
    return fromPatientDocumentData(data);
  }

  /**
   * Fetch list-row data for a specific document (documents table patching)
   * @param {string} documentId
   * @returns {Promise<Object>} Document list data
   */
  async fetchDocumentListData(documentId, options = {}) {
    const data = await fetchDocumentByPath(this.apiClient, documentId, 'list_data', options);
    return fromDocumentListData(data);
  }

  /**
   * Fetch file for a specific document
   * @param {string} documentId 
   * @returns {Promise<Blob>} Document file as blob
   */
  async fetchDocumentFile(documentId) {
    return this.apiClient.getBlob(`/api/document/${documentId}/file`);
  }

  /**
   * Upload a document
   * @param {File} file 
   * @returns {Promise<Object>} Upload response
   */
  async uploadDocument(file) {
    // Read file as base64
    const encodedData = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data URL prefix
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const requestBody = {
      file_name: file.name,
      encoded_data: encodedData
    };

    return this.apiClient.post('/api/document/upload', requestBody);
  }

  /**
   * Regenerate a document
   * @param {string} documentId 
   * @returns {Promise<Object>} Response data
   */
  async regenerateDocument(documentId) {
    return this.apiClient.post(`/api/document/${documentId}/regenerate`);
  }

  /**
   * Regenerate specific content for a document
   * @param {string} documentId 
   * @param {string} contentType 
   * @returns {Promise<Object>} Response data
   */
  async regenerateDocumentContent(documentId, contentType) {
    return this.apiClient.post(`/api/document/${documentId}/regenerate?content_type=${contentType}`);
  }

  /**
   * Search among patient's pre-existing codes
   * @param {string} documentId 
   * @param {string} query 
   * @returns {Promise<Array>} Search results
   */
  async searchDocumentCodes(documentId, query) {
    const encodedQuery = encodeURIComponent(query);
    return this.apiClient.get(`/api/document/${documentId}/codes/search?query=${encodedQuery}`);
  }

  /**
   * Search the entire SNOMED database for codes
   * @param {string} query 
   * @returns {Promise<Array>} Search results
   */
  async searchAllCodes(query) {
    const encodedQuery = encodeURIComponent(query);
    return this.apiClient.get(`/api/codes/search?query=${encodedQuery}`);
  }

  /**
   * Fetch patient data for a specific document
   * @param {string} documentId 
   * @returns {Promise<Object>} Patient data
   */
  async fetchPatientData(documentId, options = {}) {
    const data = await this.apiClient.get(`/api/document/${documentId}/patient/data`, options);
    return data == null ? null : fromPatientData(data);
  }

  /**
   * Fetch patient data for many documents in one request.
   * @param {string[]} documentIds
   * @returns {Promise<Object<string, Object|null>>} documentId -> patient
   */
  async fetchPatientsData(documentIds, options = {}) {
    const data = await this.apiClient.post('/api/documents/patient/data', documentIds, options);
    if (data == null) return data;
    const patients = {};
    Object.entries(data).forEach(([documentId, patient]) => {
      patients[documentId] = patient == null ? null : fromPatientData(patient);
    });
    return patients;
  }

  /**
   * Fetch patient record data (patient, codes, document previews) for a specific document
   * @param {string} documentId 
   * @returns {Promise<Object>} Patient record data
   */
  async fetchPatientRecordData(documentId, options = {}) {
    const data = await this.apiClient.get(`/api/document/${documentId}/patient_record/data`, options);
    return data == null ? null : fromPatientRecordData(data);
  }

  /**
   * Fetch fresh patient problem codes from EMIS for a document (1-hour cache).
   * @param {string} documentId
   * @returns {Promise<Object>} PatientCodesData
   */
  async fetchFreshPatientCodes(documentId, options = {}) {
    const data = await this.apiClient.get(`/api/document/${documentId}/patient_codes/fresh_data`, options);
    return data == null ? null : fromPatientCodesData(data);
  }

  /**
   * Add a comment to a document
   * @param {string} documentId 
   * @param {string} comment 
   * @returns {Promise<Object>} Response data
   */
  async addDocumentComment(documentId, comment) {
    const requestBody = {
      documentId: documentId,
      comment: comment
    };
    return this.apiClient.post(`/api/document/${documentId}/comment/add`, requestBody);
  }

  /**
   * Search for documents
   * @param {string} query 
   * @returns {Promise<Array>} Search results
   */
  async searchDocuments(query, options = {}) {
    const requestBody = {
      query: query
    };
    const data = await this.apiClient.post('/api/documents/search', requestBody, options);
    return asDocumentList(data);
  }

  /**
   * Fetch duplicate document IDs for a specific document
   * @param {string} documentId 
   * @returns {Promise<Array<string>>} Array of duplicate document IDs
   */
  async fetchDocumentDuplicates(documentId, options = {}) {
    try {
      const data = await this.apiClient.get(`/api/document/${documentId}/duplicates/fetch`, options);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      // A cancellation is the caller's decision, not a failure to report as "no duplicates".
      if (isAbortError(error)) {
        throw error;
      }
      console.error('Error fetching document duplicates:', error);
      return [];
    }
  }

  /**
   * Rotate a document to the specified absolute rotation angle
   * @param {string} documentId 
   * @param {number} absoluteDegrees - Rotation angle (0, 90, 180, or 270)
   * @returns {Promise<Object>} Response data
   */
  async rotateDocument(documentId, absoluteDegrees) {
    // Validate rotation angle
    if (![0, 90, 180, 270].includes(absoluteDegrees)) {
      throw new Error(`Invalid rotation angle: ${absoluteDegrees}. Must be 0, 90, 180, or 270.`);
    }
    
    return this.apiClient.post(`/api/document/${documentId}/file/rotate?absolute_degrees=${absoluteDegrees}`);
  }
}

